import React, { useState, useEffect } from 'react';
import { Trophy, Plus, Users, CheckCircle, BarChart2, Edit2, Trash2, Save } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import AdminCard from '../../components/admin/AdminCard';
import AdminModal from '../../components/admin/AdminModal';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/ChallengesPage.css';
import '../../styles/admin-shared.css';

const ChallengesPage = () => {
  // Re-fetch on program switch (X-Program already sent by axios interceptor)
  const { slug } = useProgram();
  const [challenges, setChallenges] = useState([]);
  const [stats, setStats] = useState({
    totalChallenges: 0,
    activeChallenges: 0,
    totalParticipants: 0,
    completionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_days: 7,
    difficulty: 'MEDIUM',
    start_date: new Date().toISOString().split('T')[0],
    rewards: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [challengesData, statsData] = await Promise.all([
        api.get('/admin/challenges').catch(() => ({ data: { data: { challenges: [] } } })),
        api.get('/admin/challenges/stats').catch(() => ({ data: { data: {} } }))
      ]);

      setChallenges(challengesData.data.data?.challenges || []);
      setStats(statsData.data.data || {
        totalChallenges: 0,
        activeChallenges: 0,
        totalParticipants: 0,
        completionRate: 0
      });
    } catch (error) {
      console.error('Erro ao carregar desafios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (challenge = null) => {
    if (challenge) {
      setEditing(challenge);
      setFormData({
        title: challenge.title,
        description: challenge.description || '',
        duration_days: challenge.duration_days,
        difficulty: challenge.difficulty || 'MEDIUM',
        start_date: format(new Date(challenge.start_date), 'yyyy-MM-dd'),
        rewards: typeof challenge.rewards === 'object' ? JSON.stringify(challenge.rewards) : challenge.rewards || ''
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        description: '',
        duration_days: 7,
        difficulty: 'MEDIUM',
        start_date: new Date().toISOString().split('T')[0],
        rewards: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const challengeData = {
        ...formData,
        duration_days: parseInt(formData.duration_days),
        rewards: formData.rewards ? JSON.parse(formData.rewards) : {}
      };

      if (editing) {
        await api.put(`/admin/challenges/${editing.id}`, challengeData);
      } else {
        await api.post('/admin/challenges', challengeData);
      }
      
      setShowModal(false);
      setEditing(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar desafio:', error);
      alert(error.response?.data?.message || 'Erro ao salvar desafio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este desafio?')) return;
    
    try {
      await api.delete(`/admin/challenges/${id}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir desafio:', error);
      alert(error.response?.data?.message || 'Erro ao excluir desafio');
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Desafio',
      render: (value, row) => (
        <div>
          <div className="challenge-title">{value}</div>
          <div className="challenge-duration">{row.duration_days} dias • {row.difficulty}</div>
        </div>
      )
    },
    {
      key: 'participants',
      label: 'Participantes',
      render: (value, row) => `${row.participant_count || 0} participantes`
    },
    {
      key: 'start_date',
      label: 'Início',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    },
    {
      key: 'end_date',
      label: 'Término',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => {
        const now = new Date();
        const start = new Date(row.start_date);
        const end = new Date(row.end_date);
        
        let status = 'Em breve';
        let className = 'info';
        
        if (now >= start && now <= end) {
          status = 'Ativo';
          className = 'success';
        } else if (now > end) {
          status = 'Finalizado';
          className = 'danger';
        }
        
        return <span className={`badge badge-${className}`}>{status}</span>;
      }
    }
  ];

  return (
    <AdminLayout>
      <div className="challenges-page">
        <div className="page-header">
          <div>
            <h1><Trophy size={26} /> Desafios</h1>
            <p>Gerencie desafios e acompanhe o progresso dos participantes</p>
          </div>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Criar Novo Desafio
          </button>
        </div>

        <div className="analytics-cards-grid">
          <AdminCard
            title="Total de Desafios"
            value={stats.totalChallenges || 0}
            subtitle={`${stats.activeChallenges || 0} ativos`}
            icon={<Trophy size={22} />}
            color="purple"
          />
          <AdminCard
            title="Participantes"
            value={stats.totalParticipants || 0}
            subtitle="Total de participações"
            icon={<Users size={22} />}
            color="blue"
          />
          <AdminCard
            title="Taxa de Conclusão"
            value={`${stats.completionRate || 0}%`}
            subtitle="Desafios completados"
            icon={<CheckCircle size={22} />}
            color="green"
          />
          <AdminCard
            title="Média de Participantes"
            value={stats.avgParticipants || 0}
            subtitle="Por desafio"
            icon={<BarChart2 size={22} />}
            color="yellow"
          />
        </div>

        <div className="table-section">
          <AdminTable
            columns={columns}
            data={challenges}
            loading={loading}
            actions={(row) => (
              <div className="table-actions">
                <button
                  className="table-action-btn edit"
                  onClick={() => handleOpenModal(row)}
                  title="Editar desafio"
                >
                  <Edit2 size={14} /> Editar
                </button>
                <button
                  className="table-action-btn delete"
                  onClick={() => handleDelete(row.id)}
                  title="Excluir desafio"
                >
                  <Trash2 size={14} /> Excluir
                </button>
              </div>
            )}
          />
        </div>

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          title={editing ? <><Edit2 size={20} /> Editar Desafio</> : <><Plus size={20} /> Criar Novo Desafio</>}
        >
          <form onSubmit={handleSave} className="challenge-form">
            <div className="form-group">
              <label htmlFor="title">Título do Desafio *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: 7 Dias Sem Pornografia, 30 Dias de Abstinência"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva os objetivos e regras do desafio..."
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration_days">Duração (dias) *</label>
                <input
                  type="number"
                  id="duration_days"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                  min="1"
                  max="365"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="difficulty">Dificuldade *</label>
                <select
                  id="difficulty"
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  required
                >
                  <option value="EASY">Fácil</option>
                  <option value="MEDIUM">Médio</option>
                  <option value="HARD">Difícil</option>
                  <option value="EXPERT">Expert</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="start_date">Data de Início *</label>
              <input
                type="date"
                id="start_date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="rewards">Recompensas (JSON)</label>
              <textarea
                id="rewards"
                value={formData.rewards}
                onChange={(e) => setFormData({ ...formData, rewards: e.target.value })}
                placeholder='{"points": 100, "badge": "7_days_streak"}'
                rows="3"
              />
              <small className="form-hint">Digite as recompensas em formato JSON</small>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editing ? <><Save size={16} /> Salvar Alterações</> : <><Plus size={16} /> Criar Desafio</>}
              </button>
            </div>
          </form>
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default ChallengesPage;
